import { useState, useEffect } from 'react';
import Header from './Header.js';
import { useNavigate } from 'react-router-dom';

const NewsAdmin = ({ loggedInUser, setLoggedInUser }) => {
    const navigate = useNavigate();
    const [submitting, setSubmitting] = useState(false);
    const [message, setMessage] = useState('');
    const [form, setForm] = useState({
        title: '', summary: '', description: '', image: '',
        link: '', author: loggedInUser?.name || '', type: 'blog',
        tags: '', sourceUrl: '', sourcePlatform: ''
    });

    const token = localStorage.getItem('token');

    // Redirect if not admin
    useEffect(() => {
        if (loggedInUser === null) return; // wait for user to load
        if (!loggedInUser) {
            navigate('/login');
        } else if (loggedInUser.role !== 'ADMIN') {
            navigate('/news');
        }
    }, [loggedInUser, navigate]);

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        setMessage('');
        try {
            const body = {
                ...form,
                tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
                sourcePlatform: form.sourcePlatform || null,
                sourceUrl: form.sourceUrl || null,
                image: form.image || null,
                link: form.link || null,
            };
            const res = await fetch('https://vicktoria.ai/api/news', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(body)
            });
            if (res.ok) {
                setMessage('✅ Post created successfully!');
                setForm({ title: '', summary: '', description: '', image: '', link: '', author: loggedInUser?.name || '', type: 'blog', tags: '', sourceUrl: '', sourcePlatform: '' });
            } else {
                const err = await res.json();
                setMessage(`❌ Error: ${err.error}`);
            }
        } catch (err) {
            setMessage('❌ Failed to create post.');
        } finally {
            setSubmitting(false);
        }
    };

    const inputStyle = {
        width: '100%', padding: '10px', borderRadius: '8px',
        border: '1px solid #a0bfce', fontFamily: 'Inter, sans-serif',
        fontSize: '14px', boxSizing: 'border-box', backgroundColor: '#fff'
    };

    const labelStyle = {
        fontFamily: 'Inter, sans-serif', fontSize: '13px',
        fontWeight: '600', color: '#000', marginBottom: '4px', display: 'block'
    };

    return (
        <div style={{ backgroundColor: '#D1DEE6', minHeight: '100vh' }}>
            <Header
                loggedInUser={loggedInUser}
                setLoggedInUser={setLoggedInUser}
                topText=""
                midText="News Admin"
                botText="Manage news posts"
            />

            <section style={{ display: 'flex', justifyContent: 'center', padding: '0 5% 40px 5%' }}>
                <div style={{ width: '100%', maxWidth: '900px', display: 'flex', flexDirection: 'column', gap: '30px' }}>

                    {/* CREATE POST FORM */}
                    <div style={{ backgroundColor: '#81B2CA', borderRadius: '20px', padding: '30px' }}>
                        <h2 style={{ fontFamily: 'Inter, sans-serif', margin: '0 0 20px 0' }}>Create New Post</h2>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                            <div><label style={labelStyle}>Title *</label><input style={inputStyle} name="title" value={form.title} onChange={handleChange} /></div>
                            <div><label style={labelStyle}>Summary *</label><textarea style={{ ...inputStyle, height: '80px' }} name="summary" value={form.summary} onChange={handleChange} /></div>
                            <div><label style={labelStyle}>Description *</label><textarea style={{ ...inputStyle, height: '120px' }} name="description" value={form.description} onChange={handleChange} /></div>
                            <div><label style={labelStyle}>Author *</label><input style={inputStyle} name="author" value={form.author} onChange={handleChange} /></div>
                            <div>
                                <label style={labelStyle}>Type *</label>
                                <select style={inputStyle} name="type" value={form.type} onChange={handleChange}>
                                    <option value="blog">Blog</option>
                                    <option value="social">Social</option>
                                    <option value="update">Update</option>
                                </select>
                            </div>
                            <div><label style={labelStyle}>Tags (comma separated)</label><input style={inputStyle} name="tags" value={form.tags} onChange={handleChange} placeholder="ai, news, update" /></div>
                            <div><label style={labelStyle}>Image URL</label><input style={inputStyle} name="image" value={form.image} onChange={handleChange} /></div>
                            <div><label style={labelStyle}>Link</label><input style={inputStyle} name="link" value={form.link} onChange={handleChange} /></div>
                            <div><label style={labelStyle}>Source URL (social only)</label><input style={inputStyle} name="sourceUrl" value={form.sourceUrl} onChange={handleChange} /></div>
                            <div>
                                <label style={labelStyle}>Source Platform (social only)</label>
                                <select style={inputStyle} name="sourcePlatform" value={form.sourcePlatform} onChange={handleChange}>
                                    <option value="">None</option>
                                    <option value="twitter">Twitter</option>
                                    <option value="instagram">Instagram</option>
                                    <option value="linkedin">LinkedIn</option>
                                    <option value="facebook">Facebook</option>
                                </select>
                            </div>

                            {message && <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', margin: 0 }}>{message}</p>}

                            <button
                                onClick={handleSubmit}
                                disabled={submitting}
                                style={{
                                    backgroundColor: '#4a7094ff', color: '#fff',
                                    border: 'none', borderRadius: '10px', padding: '12px',
                                    fontFamily: 'Inter, sans-serif', fontWeight: '600',
                                    fontSize: '15px', cursor: 'pointer'
                                }}>
                                {submitting ? 'Creating...' : 'Create Post'}
                            </button>
                        </div>
                    </div>

                    {/* Existing posts (edit / pin / delete) are managed inline
                        on the /news page. Navigate back to manage posts. */}
                    <button
                        onClick={() => navigate('/news')}
                        style={{
                            alignSelf: 'flex-start',
                            backgroundColor: 'transparent', color: '#4a7094ff',
                            border: 'none', cursor: 'pointer',
                            fontFamily: 'Inter, sans-serif', fontSize: '14px', fontWeight: '600'
                        }}
                    >
                        ← Back to News
                    </button>

                </div>
            </section>

            <link rel="preconnect" href="https://fonts.googleapis.com" />
            <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
            <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Judson:wght@400;700&display=swap" rel="stylesheet" />
        </div>
    );
};

export default NewsAdmin;